import { useDebounceFn } from '@vueuse/core';
import { ref, toRef } from 'vue';
import type {
  CurrencyListQuery,
  HotelCategorySuggestionsQuery,
  HotelTypeSuggestionsQuery,
  RoomTypeSuggestionsQuery,
  TownSuggestionQuery,
} from '@/graphql/generated';
import { useServices } from '@/core/composables/services';
import {
  type AddressSuggestion as AddressSuggestionType,
  type LegalSuggestion as LegalSuggestionType,
} from '@/core/types/suggestions';
import { CurrencyList, TownSuggestion } from '@/graphql/queries/helpers.gql';
import { AddressSuggestion, LegalSuggestion } from '@/graphql/rest/suggestions';
import {
  HotelCategorySuggestions,
  HotelTypeSuggestions,
  RoomTypeSuggestions,
} from '@/parts/objects/gql/accommodationObjects.gql';

const useSuggestionsFunction = <T extends unknown[]>(
  getSuggestionsFunction: (key: string) => Promise<T>,
  options?: {
    immediate?: boolean;
    defaultKey?: string;
  },
) => {
  const isLoading = ref(false);

  const suggestions = ref<T>();

  const getSuggestions = useDebounceFn(async (key?: string) => {
    isLoading.value = true;

    suggestions.value = await getSuggestionsFunction(key || options?.defaultKey || '');

    isLoading.value = false;
  }, 500);

  if (options?.immediate) {
    void getSuggestions(options.defaultKey || '');
  }

  return {
    isLoading,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    suggestions: toRef(() => suggestions.value || ([] as T)),
    getSuggestions,
  };
};

export const useSuggestions = () => {
  const { $api } = useServices();

  const useTownSuggestions = () =>
    useSuggestionsFunction<
      {
        title: string;
        value: string;
      }[]
    >(
      async (key: string) => {
        const result = await $api.query<TownSuggestionQuery>(TownSuggestion, {
          key,
          maxResults: 50,
        });

        if (!result.data) {
          return [];
        }

        return result.data.townSuggestions.map((item) => ({
          title: item.toponymsPath.join(', '),
          value: item.cid,
        }));
      },
      {
        immediate: true,
        defaultKey: 'М',
      },
    );

  const useDadataAddressSuggestions = () =>
    useSuggestionsFunction<AddressSuggestionType[]>(
      async (query: string): Promise<AddressSuggestionType[]> => {
        const result = await $api.query<{
          addressSuggestion: {
            suggestions: AddressSuggestionType[] | null;
          };
        }>(AddressSuggestion, { query });

        if (!result.data) {
          return [];
        }

        return result.data.addressSuggestion.suggestions || [];
      },
      {
        immediate: true,
      },
    );

  const getDadataAddressSuggestions = async (query: string): Promise<AddressSuggestionType[]> => {
    const result = await $api.query<{
      addressSuggestion: {
        suggestions: AddressSuggestionType[] | null;
      };
    }>(AddressSuggestion, { query });

    if (!result.data) {
      return [];
    }

    return result.data.addressSuggestion.suggestions || [];
  };

  const getLegalSuggestions = async (query: string, type?: 'LEGAL' | 'INDIVIDUAL'): Promise<LegalSuggestionType[]> => {
    const variables: { query: string; type?: string } = { query };

    if (type) {
      variables.type = type;
    }

    const result = await $api.query<{
      legalSuggestion: {
        suggestions: LegalSuggestionType[] | null;
      };
    }>(LegalSuggestion, variables);

    if (!result.data) {
      return [];
    }

    return result.data.legalSuggestion.suggestions || [];
  };

  const useHotelCategorySuggestions = () =>
    useSuggestionsFunction<HotelCategorySuggestionsQuery['hotelCategorySuggestions']>(
      async (key: string) => {
        const result = await $api.query<HotelCategorySuggestionsQuery>(HotelCategorySuggestions, {
          key,
          maxResults: 50,
        });

        if (!result.data) {
          return [];
        }

        return result.data.hotelCategorySuggestions;
      },
      {
        immediate: true,
      },
    );

  const useHotelTypeSuggestions = () =>
    useSuggestionsFunction<HotelTypeSuggestionsQuery['hotelTypeSuggestions']>(
      async (key: string) => {
        const result = await $api.query<HotelTypeSuggestionsQuery>(HotelTypeSuggestions, {
          key,
          maxResults: 50,
        });

        if (!result.data) {
          return [];
        }

        return result.data.hotelTypeSuggestions;
      },
      {
        immediate: true,
      },
    );

  const getHotelTypeSuggestions = async (key: string) => {
    const result = await $api.query<HotelTypeSuggestionsQuery>(HotelTypeSuggestions, {
      key,
      maxResults: 50,
    });

    if (!result.data) {
      return [];
    }

    return result.data.hotelTypeSuggestions;
  };

  const roomTypeSuggestions = () =>
    useSuggestionsFunction<RoomTypeSuggestionsQuery['roomTypeSuggestions']>(async (key: string) => {
      const result = await $api.query<RoomTypeSuggestionsQuery>(RoomTypeSuggestions, {
        key,
        maxResults: 50,
      });

      if (!result.data) {
        return [];
      }

      return result.data.roomTypeSuggestions;
    });

  const getCurrencyList = async () => {
    const result = await $api.query<CurrencyListQuery>(CurrencyList);

    if (!result.data) {
      return [];
    }

    return result.data.currencyList;
  };

  return {
    useTownSuggestions,
    useHotelCategorySuggestions,
    getHotelTypeSuggestions,
    getDadataAddressSuggestions,
    useHotelTypeSuggestions,
    getLegalSuggestions,
    roomTypeSuggestions,
    getCurrencyList,
    useDadataAddressSuggestions,
  };
};
